// -----------------------------------------------------------
// FloraIQ - Fluxo Operacional (Página Principal)
// -----------------------------------------------------------

import { useState, useCallback } from "react";
import PageHeader from "@/components/shared/PageHeader";
import KpiCard from "@/components/shared/KpiCard";
import { operationalKpis, timelineStages } from "@/mocks/operational";
import type { CadastralAction } from "@/mocks/operational";
import OperationalOverview from "./components/OperationalOverview";
import CadastralActionsSection from "./components/CadastralActionsSection";
import PlanningStagePanel from "./components/PlanningStagePanel";
import BaseSurveyStagePanel from "./components/BaseSurveyStagePanel";
import FieldCollectionStagePanel from "./components/FieldCollectionStagePanel";
import Processing3DStagePanel from "./components/Processing3DStagePanel";
import LadmModelingStagePanel from "./components/LadmModelingStagePanel";
import ValidationStagePanel from "./components/ValidationStagePanel";
import PublicationStagePanel from "./components/PublicationStagePanel";
import DeliverablesGrid from "./components/DeliverablesGrid";
import OperationalReportsGrid from "./components/OperationalReportsGrid";
import NewCadastralActionModal from "./components/NewCadastralActionModal";
import StageDetailDrawer from "./components/StageDetailDrawer";
import { planningChecklist, planningPendencias, baseSurveyChecklist } from "@/mocks/operational";

type TabId = "overview" | "actions" | "planning" | "survey" | "field" | "processing3d" | "ladm" | "validation" | "publication" | "deliverables" | "reports";

const tabs: { id: TabId; label: string; icon: string }[] = [
  { id: "overview", label: "Visão Geral", icon: "ri-eye-line" },
  { id: "actions", label: "Ações de Cadastramento", icon: "ri-folder-chart-line" },
  { id: "planning", label: "Etapa 1 — Planejamento", icon: "ri-calendar-check-line" },
  { id: "survey", label: "Etapa 2 — Levantamento Base", icon: "ri-flight-takeoff-line" },
  { id: "field", label: "Etapa 3 — Campo", icon: "ri-plant-line" },
  { id: "processing3d", label: "Etapa 4 — Process. 3D", icon: "ri-stack-line" },
  { id: "ladm", label: "Etapa 5 — Modelagem LADM", icon: "ri-node-tree" },
  { id: "validation", label: "Etapa 6 — Validação", icon: "ri-check-double-line" },
  { id: "publication", label: "Etapa 7 — Publicação", icon: "ri-earth-line" },
  { id: "deliverables", label: "Produtos", icon: "ri-box-3-line" },
  { id: "reports", label: "Relatórios", icon: "ri-file-chart-line" },
];

export default function OperationalFlowPage() {
  const [activeTab, setActiveTab] = useState<TabId>("overview");
  const [newActionOpen, setNewActionOpen] = useState(false);
  const [selectedAction, setSelectedAction] = useState<CadastralAction | null>(null);
  const [drawerStageIdx, setDrawerStageIdx] = useState(0);

  const handleSelectAction = useCallback((action: CadastralAction) => {
    setSelectedAction(action);
    setActiveTab("planning");
  }, []);

  const handleOpenStageDrawer = useCallback((stageIdx: number) => {
    setDrawerStageIdx(stageIdx);
    setActiveTab("overview");
  }, []);

  const stagesForAction = selectedAction?.stages || timelineStages;

  const renderTab = () => {
    switch (activeTab) {
      case "overview":
        return <OperationalOverview />;
      case "actions":
        return <CadastralActionsSection onViewAction={handleSelectAction} />;
      case "planning":
        return <PlanningStagePanel />;
      case "survey":
        return <BaseSurveyStagePanel />;
      case "field":
        return <FieldCollectionStagePanel />;
      case "processing3d":
        return <Processing3DStagePanel />;
      case "ladm":
        return <LadmModelingStagePanel />;
      case "validation":
        return <ValidationStagePanel />;
      case "publication":
        return <PublicationStagePanel />;
      case "deliverables":
        return <DeliverablesGrid />;
      case "reports":
        return <OperationalReportsGrid />;
      default:
        return <OperationalOverview />;
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Fluxo Operacional"
        subtitle="Planeje, acompanhe e valide todas as etapas da ação de cadastramento arbóreo, do levantamento base à publicação dos produtos técnicos."
      >
        <button
          onClick={() => setNewActionOpen(true)}
          className="btn-primary text-xs px-3 py-2 cursor-pointer whitespace-nowrap"
        >
          <i className="ri-add-line mr-1"></i>
          <span className="hidden sm:inline">Nova Ação de Cadastramento</span>
        </button>
        <button
          onClick={() => setActiveTab("planning")}
          className="btn-outline text-xs px-3 py-2 cursor-pointer whitespace-nowrap"
        >
          <i className="ri-check-double-line mr-1"></i>
          <span className="hidden sm:inline">Abrir Checklist</span>
        </button>
        <button
          onClick={() => setActiveTab("validation")}
          className="btn-outline text-xs px-3 py-2 cursor-pointer whitespace-nowrap"
        >
          <i className="ri-shield-check-line mr-1"></i>
          <span className="hidden sm:inline">Validar Etapa Atual</span>
        </button>
        <button
          onClick={() => setActiveTab("reports")}
          className="btn-outline text-xs px-3 py-2 cursor-pointer whitespace-nowrap"
        >
          <i className="ri-file-chart-line mr-1"></i>
          <span className="hidden sm:inline">Gerar Relatório</span>
        </button>
        <button className="btn-outline text-xs px-3 py-2 cursor-pointer whitespace-nowrap">
          <i className="ri-download-line mr-1"></i>
          <span className="hidden sm:inline">Exportar Plano</span>
        </button>
      </PageHeader>

      {/* KPI Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <KpiCard
          label="Ações em Andamento"
          value={operationalKpis.acoesEmAndamento}
          icon="ri-play-circle-line"
          variant="info"
        />
        <KpiCard
          label="Etapas Concluídas"
          value={operationalKpis.etapasConcluidas}
          icon="ri-check-double-line"
          variant="success"
        />
        <KpiCard
          label="Pendências Operacionais"
          value={operationalKpis.pendenciasOperacionais}
          icon="ri-alert-line"
          variant="warning"
        />
        <KpiCard
          label="Árvores Cadastradas"
          value={operationalKpis.arvoresCadastradasCampo.toLocaleString("pt-BR")}
          icon="ri-plant-line"
          variant="success"
        />
        <KpiCard
          label="Levantamentos 3D"
          value={operationalKpis.levantamentos3DProcessados}
          icon="ri-stack-line"
          variant="info"
        />
        <KpiCard
          label="Validações Aprovadas"
          value={`${operationalKpis.validacoesAprovadas}%`}
          icon="ri-check-line"
          variant={operationalKpis.validacoesAprovadas >= 85 ? "success" : "warning"}
        />
        <KpiCard
          label="Produtos Gerados"
          value={operationalKpis.produtosGerados}
          icon="ri-box-3-line"
          variant="info"
        />
        <KpiCard
          label="Bloqueios Críticos"
          value={operationalKpis.bloqueiosCriticos}
          icon="ri-error-warning-line"
          variant={operationalKpis.bloqueiosCriticos > 0 ? "danger" : "success"}
        />
      </div>

      {/* Aviso conceitual */}
      <div className="bg-accent-50/60 border border-accent-200/60 rounded-card p-4 text-sm text-foreground-600">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0 mt-0.5">
            <i className="ri-information-line text-accent-600"></i>
          </div>
          <p>
            O Fluxo Operacional organiza a ação de cadastramento arbóreo em 7 etapas sequenciais, da preparação inicial à publicação dos produtos técnicos, 
            seguindo a metodologia FloraIQ-LADM com rastreabilidade completa de responsáveis, fontes e eventos.
          </p>
        </div>
      </div>

      {/* Selected action indicator */}
      {selectedAction && activeTab !== "actions" && activeTab !== "overview" && (
        <div className="flex items-center gap-3 px-4 py-2.5 rounded-card border border-primary-200 bg-primary-50/50">
          <div className="w-7 h-7 rounded-lg bg-primary-100 flex items-center justify-center">
            <i className="ri-folder-chart-line text-primary-600 text-sm"></i>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-foreground-800">{selectedAction.nome}</p>
            <p className="text-[10px] text-foreground-500">{selectedAction.municipio}/{selectedAction.uf} • Progresso: {selectedAction.progresso}%</p>
          </div>
          <button onClick={() => setSelectedAction(null)} className="text-[10px] text-foreground-400 hover:text-foreground-600 cursor-pointer whitespace-nowrap">
            <i className="ri-close-line mr-1"></i>Limpar seleção
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white rounded-card border border-background-200/70 overflow-hidden">
        <div className="border-b border-background-200/50 overflow-x-auto scrollbar-thin">
          <div className="flex px-1 pt-1 gap-0.5 min-w-max">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-3 py-2.5 text-xs font-medium whitespace-nowrap transition-all duration-200 cursor-pointer rounded-t-md border-b-2 -mb-[1px] ${
                  activeTab === tab.id
                    ? "text-accent-600 border-accent-500 bg-accent-50/50"
                    : "text-foreground-500 border-transparent hover:text-foreground-700 hover:bg-background-50/50"
                }`}
              >
                <i className={`${tab.icon} text-sm`}></i>
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="p-5 fade-in" key={activeTab}>
          {renderTab()}
        </div>
      </div>

      {/* Modals & Drawers */}
      <NewCadastralActionModal isOpen={newActionOpen} onClose={() => setNewActionOpen(false)} />

      {stagesForAction[drawerStageIdx] && (
        <StageDetailDrawer
          isOpen={false}
          onClose={() => {}}
          stage={stagesForAction[drawerStageIdx]}
          stageLabel={stagesForAction[drawerStageIdx].nome}
          checklist={activeTab === "planning" ? planningChecklist : activeTab === "survey" ? baseSurveyChecklist : []}
          pendencias={activeTab === "planning" ? planningPendencias : []}
        />
      )}
    </div>
  );
}